/**
 * MatterDetail — overview page for a single matter.
 *
 * Route: /matter/:matterRef
 *
 * Phase 1 (this delivery) surfaces the TA-forms status panel only.
 * Future phases will fold in the other matter surfaces (CCL, conflict
 * check, deadlines, etc.) so fee-earners have a single place to land.
 */
import { useState, useEffect, useCallback } from "react";
import { setCurrentMatterRef, clearCurrentMatterRef } from "../lib/callNoteMatter";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { usePipelineCacheInvalidation } from "../hooks/usePipelineCacheInvalidation";
import { ArrowLeft, Loader2, ShieldCheck, ShieldAlert, ShieldX, Send, Mail, Receipt, CreditCard, CheckCircle2, Copy, FileText, FolderOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import BonsaiMate from "../components/BonsaiMate";
import MatterAlertsPanel from "../components/MatterAlertsPanel";
import NextStepBanner from "../components/NextStepBanner";
import CclSentOffSystem from "../components/CclSentOffSystem";
import PreCaseMinderHistoryPanel from "../components/PreCaseMinderHistoryPanel"; // BM-H4320
import MatterFileNotesCard from "../components/MatterFileNotesCard";
import MatterTimeCard from "../components/MatterTimeCard";
import MatterOverviewCard from "../components/MatterOverviewCard";
import MatterDocumentsCard from "../components/MatterDocumentsCard";
import ThirdfortReviewPanel from "../components/ThirdfortReviewPanel";
import LoaStatusPanel from "../components/LoaStatusPanel"; // BM-H3851
import LatePaymentMatterCard from "../components/LatePaymentMatterCard"; // BM-H4482
import DirectorVouchPanel from "../components/DirectorVouchPanel";
import MatterUatStrip from "../components/MatterUatStrip"; // BM-H3978
import NewMemoDialog from "../components/NewMemoDialog"; // BM-H4761
import SubmitForReviewDialog from "../components/SubmitForReviewDialog"; // BM-H5047
import { SoFSection2 } from "./SourceOfFundsForm";
import { clientFormsApi, formatSendRefusal } from "../api/clientForms";
import LpaSendDialog from "../components/client-forms/LpaSendDialog";
import WillConfirmationsPanel from "../components/client-forms/WillConfirmationsPanel"; // BM-H3879
import { errorMessage } from "../utils/errorMessage";
import { obSendWithheldReason } from "../utils/obSendGuard";

// Brief 77 Drift 3 (Option B) — the FE-input stage realised as a first-class
// UI card. The backend pipeline (PIPELINE_STAGES / advance_pipeline) is
// unchanged; this card surfaces the fee-earner field that must be completed
// before the onboarding link can be sent, and gates the "Send Onboarding
// Link" action on the matter page itself (previously only on the Dashboard /
// Pipeline Tracker). The matter is treated as cleared/ready for OB once a
// conflict check exists for it; the client email is the FE-input field.
// BM-H3407 (Vanessa, 1 Oct 2026): "where a matter has joint clients, can all
// screens please name both". The header named the reference only; this line
// names every client, "Smith & Jones", from the same status payload the
// onboarding panel reads.
function MatterClientLine({ matterRef, apiBase }) {
    const [names, setNames] = useState("");
    useEffect(() => {
        let live = true;
        axios.get(`${apiBase}/api/pipeline/matter/${encodeURIComponent(matterRef)}/status`)
            .then((r) => { if (live) setNames(r.data?.client_display_name || r.data?.client_name || ""); })
            .catch(() => { if (live) setNames(""); });
        return () => { live = false; };
    }, [matterRef, apiBase]);
    if (!names) return null;
    return (
        <p className="text-sm text-slate-300 mt-0.5" data-testid="matter-detail-clients">{names}</p>
    );
}

function MatterOnboardingPanel({ matterRef, apiBase }) {
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(false);
    const [clientEmail, setClientEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [sendError, setSendError] = useState("");

    const fetchStatus = async () => {
        try {
            const r = await axios.get(`${apiBase}/api/pipeline/matter/${encodeURIComponent(matterRef)}/status`);
            setStatus(r.data || null);
            setStatusError(false);
            const existing = r.data?.client_email || r.data?.primary_client_email;
            if (existing) setClientEmail(existing);
        } catch (_e) {
            setStatusError(true);
        }
    };

    useEffect(() => { fetchStatus(); }, [matterRef]); // eslint-disable-line react-hooks/exhaustive-deps

    // BM-H278 (Vanessa, 4 Sep 2026): the matter card offered "Send
    // Onboarding Link" on every matter, including ones whose OB was already
    // returned, returned offline, skipped or satisfied via a linked matter.
    // The backend now ships the answer on the status payload; the card
    // states it instead of rendering the send form.
    const obWithheld = obSendWithheldReason(status);

    const emailLooksValid = /.+@.+\..+/.test(clientEmail.trim());
    const canSend = emailLooksValid && !sending;

    const handleSend = async () => {
        setSending(true);
        setSendError("");
        try {
            await axios.post(`${apiBase}/api/pipeline/send-ob`, {
                matter_ref: matterRef,
                client_email: clientEmail.trim(),
            });
            setSent(true);
            fetchStatus();
        } catch (e) {
            setSendError(errorMessage(e, "Could not send the onboarding link."));
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"
                 data-testid="matter-fe-input-card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                    <Send className="w-4 h-4 text-teal" />
                    Fee-Earner Input — Client Onboarding
                </h2>
                {status?.stage_label && (
                    <span className="text-xs px-2 py-0.5 rounded bg-teal-dim/30 text-teal border border-teal/40"
                          data-testid="matter-pipeline-stage-label">
                        {status.stage_label}
                    </span>
                )}
            </div>

            {obWithheld ? (
                <p className="text-sm text-teal" data-testid="matter-ob-satisfied">
                    {obWithheld}
                </p>
            ) : (
            <>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">
                Complete the fields below before the onboarding link can be sent. The
                "Send Onboarding Link" button unlocks once a client email is on file.
            </p>

            <div className="space-y-3">
                <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">
                        Client email <span className="text-status-red">*</span>
                    </label>
                    <Input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="client@example.com"
                        className="bg-white"
                        data-testid="matter-ob-client-email"
                    />
                </div>

                {sent && (
                    <p className="text-sm text-teal flex items-center gap-1.5" data-testid="matter-ob-sent-confirmation">
                        <CheckCircle2 className="w-4 h-4" /> Onboarding link sent to {clientEmail.trim()}.
                    </p>
                )}
                {sendError && (
                    <p className="text-sm text-status-red" data-testid="matter-ob-send-error">{sendError}</p>
                )}
                {statusError && (
                    <p className="text-xs text-slate-500" data-testid="matter-pipeline-status-error">
                        Pipeline status unavailable — you can still send the link below.
                    </p>
                )}

                <Button
                    onClick={handleSend}
                    disabled={!canSend}
                    className="bg-teal hover:bg-teal"
                    data-testid="matter-send-ob-btn"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Onboarding Link
                </Button>
            </div>
            </>
            )}
        </section>
    );
}

function MatterShareholdersAgreementPanel({ matterRef }) {
    const [creating, setCreating] = useState(false);
    const [link, setLink] = useState("");
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const createLink = async () => {
        setCreating(true);
        setError("");
        setCopied(false);
        try {
            const response = await axios.post(`${API}/api/bonsai-pen/client-link/create`, {
                matter_reference: matterRef,
            });
            setLink(response.data?.url || "");
        } catch (e) {
            setError(errorMessage(e, "Could not create the questionnaire link."));
        } finally {
            setCreating(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
        } catch {
            setError("Copy the link from the field below.");
        }
    };

    return (
        <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"
                 data-testid="matter-sha-questionnaire-card">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-teal" />
                Shareholders&apos; Agreement
            </h2>
            <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                Create a secure questionnaire link for the client. Send the copied link manually;
                no email is sent automatically.
            </p>
            {error && <p className="mt-3 text-sm text-status-red" data-testid="matter-sha-questionnaire-error">{error}</p>}
            {link ? (
                <div className="mt-4 flex gap-2">
                    <Input readOnly value={link} aria-label="Shareholders agreement questionnaire link"
                           data-testid="matter-sha-questionnaire-link" className="bg-white text-slate-900" />
                    <Button type="button" variant="outline" onClick={copyLink}
                            data-testid="matter-sha-questionnaire-copy-btn">
                        <Copy className="w-4 h-4 mr-1.5" />{copied ? "Copied" : "Copy"}
                    </Button>
                </div>
            ) : (
                <Button onClick={createLink} disabled={creating} className="mt-4 bg-teal hover:bg-teal"
                        data-testid="matter-send-sha-questionnaire-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send shareholders agreement questionnaire to client
                </Button>
            )}
        </section>
    );
}

// BM-H90 — Private Client questionnaire cards (Wills / LPAs / EPR). Copied
// from MatterShareholdersAgreementPanel with three differences: the card is
// always visible for its matter type (not just when unsent), the state pill
// shows the current lifecycle stage, and the send/view/download actions all
// remain available at every stage so the fee earner can resend, revoke and
// re-issue, or re-download.
function PrivateClientQuestionnaireCard({
    matterRef,
    kind,           // "wills" | "lpa" | "epr"
    label,          // e.g. "Wills"
    initialForms,   // array of form-instance rows from listForMatter
    onChanged,      // callback so the parent can refresh the list
    defaultClientEmail = "",   // BM-H3707: the matter's client email, to fill in the LPA send form
    defaultClientName = "",    // BM-H4005: the matter's client name, so the invite can greet them by name
}) {
    // engine_kind on the backend is "wills", "lpa", or "epr". The list
    // endpoint returns every form on the matter regardless of kind, so we
    // filter here for the card's own kind.
    const forms = (initialForms || []).filter((f) => (f.engine_kind || "") === kind);
    // Prefer the most-recently-created active instance for the state badge.
    const active = forms.slice().sort(
        (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
    )[0] || null;

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const [link, setLink] = useState("");
    const [lpaDialogOpen, setLpaDialogOpen] = useState(false);
    const [lpaFeeEarner, setLpaFeeEarner] = useState(null);   // BM-H4003: the matter's fee earner, read when the dialog opens

    const stateBadge = (() => {
        if (!active) return { text: "Not sent", tone: "slate" };
        const s = (active.state || "").toLowerCase();
        const dt = (iso) => (iso || "").slice(0, 10);
        if (s === "finalised") return { text: `Finalised ${dt(active.finalised_at)}`, tone: "teal" };
        if (s === "submitted") return { text: `Submitted ${dt(active.submitted_at)}`, tone: "teal" };
        if (s === "revoked")   return { text: "Revoked", tone: "red" };
        // BM-H4141: a re-opened questionnaire read "Sent <the day it was first sent>", the same as one never
        // submitted, so the badge did not change when staff clicked Re-open for client (UAT Script 2 Part B step 3).
        if (active.reopened_at) return { text: `Re-opened for client ${dt(active.reopened_at)}`, tone: "amber" };
        return { text: `Sent ${dt(active.created_at)}`, tone: "amber" };
    })();

    const badgeClass = {
        slate: "bg-white/[0.04] text-slate-300 border-white/10",
        amber: "bg-status-amber/10 text-status-amber border-status-amber/30",
        teal:  "bg-teal-dim/30 text-teal border-teal/40",
        red:   "bg-status-red/10 text-status-red border-status-red/30",
    }[stateBadge.tone];

    const createEndpoint = async (payload) => {
        if (kind === "wills") return clientFormsApi.createWillsForm(payload);
        // BM-H3707: LPAs are sent from LpaSendDialog (sendLpa below), never from prompts.
        if (kind === "epr") return clientFormsApi.createEprForm(payload);
        throw new Error(`unknown kind ${kind}`);
    };

    // BM-H3707: a form, not three browser pop-ups. The LPA type is two clear choices with
    // none pre-selected, so a typo can no longer quietly create the wrong LPA.
    // BM-H4005: sent only when the matter's client name is known; the invite then greets them by name, and without it
    // the greeting has no name in it (never "Dear there").
    const nameField = defaultClientName ? { client_name: defaultClientName } : {};
    const sendLpa = async (values) => {
        setBusy(true);
        setError("");
        setCopied(false);
        try {
            const resp = await clientFormsApi.createLpaForm({ matter_ref: matterRef, ...nameField, ...values });
            setLink(resp?.data?.client_link || "");
            setLpaDialogOpen(false);
            if (onChanged) onChanged();
        } catch (e) {
            // BM-H339: the backend refuses with 403 and a structured detail when the file is not open.
            setLpaDialogOpen(false);
            setError(formatSendRefusal(e?.response?.data?.detail, label));
        } finally {
            setBusy(false);
        }
    };

    // BM-H4003: the fee earner a questionnaire is sent in the name of (and from the mailbox of) is the MATTER's fee earner,
    // read from the matter and shown, never typed. Typing a name here used to make anyone signed in the owner of the form.
    // Returns the fee earner, or null after saying why there is none.
    const loadMatterFeeEarner = async () => {
        try {
            const { data } = await clientFormsApi.getMatterFeeEarner(matterRef);
            if (data && data.determined && data.fee_earner_email) return data;
            setError((data && data.message) || "We cannot tell who the fee earner on this matter is, so the questionnaire has not been sent.");
        } catch (e) {
            setError(errorMessage(e, "We could not find out who the fee earner on this matter is, so the questionnaire has not been sent."));
        }
        return null;
    };

    const handleSend = async () => {
        if (kind === "lpa") {
            setError("");
            setCopied(false);
            setBusy(true);
            const fe = await loadMatterFeeEarner();
            setBusy(false);
            if (!fe) return;
            setLpaFeeEarner(fe);
            setLpaDialogOpen(true);
            return;
        }
        setBusy(true);
        setError("");
        setCopied(false);
        try {
            // The review questionnaire (epr) is sent by a different route that still asks for the address; the matter-owner
            // rule of BM-H4003 covers the wills and LPA questionnaires.
            let matterFeeEarner = null;
            if (kind === "wills") {
                matterFeeEarner = await loadMatterFeeEarner();
                if (!matterFeeEarner) {
                    setBusy(false);
                    return;
                }
            }
            const clientEmail = window.prompt(`Client email for the ${label} questionnaire`) || "";
            if (!clientEmail.trim()) {
                setBusy(false);
                return;
            }
            let feeEarnerEmail;
            if (matterFeeEarner) {
                const who = matterFeeEarner.fee_earner_name
                    ? `${matterFeeEarner.fee_earner_name} (${matterFeeEarner.fee_earner_email})`
                    : matterFeeEarner.fee_earner_email;
                const ok = window.confirm(
                    `The ${label} questionnaire will be sent to ${clientEmail.trim()} from the mailbox of ${who}, the fee earner on this matter. Send it?`,
                );
                if (!ok) {
                    setBusy(false);
                    return;
                }
                feeEarnerEmail = matterFeeEarner.fee_earner_email;
            } else {
                // epr only: the workbook route still takes the address.
                feeEarnerEmail = window.prompt("Your fee-earner email (from address)") || "";
            }
            const resp = await createEndpoint({
                matter_ref: matterRef,
                client_email: clientEmail.trim(),
                fee_earner_email: feeEarnerEmail.trim(),
                // the Wills create route takes the client's name; the Estate Planning Review one is a different route
                ...(kind === "wills" ? nameField : {}),
            });
            setLink(resp?.data?.client_link || "");
            if (onChanged) onChanged();
        } catch (e) {
            // BM-H339: the backend refuses the send with 403 and a structured
            // detail when the file is not properly open. Name what is
            // outstanding rather than showing a bare failure, and never render
            // the detail object straight into the DOM.
            setError(formatSendRefusal(e?.response?.data?.detail, label));
        } finally {
            setBusy(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
        } catch {
            setError("Copy the link from the field below.");
        }
    };

    const openReview = () => {
        if (!active) return;
        // BM-H354: App.js registers this under the matter, not at a bare
        // /fee-earner path. The old URL fell through to the not-found handler
        // for wills, LPA and review questionnaires alike.
        window.open(
            `/matters/${encodeURIComponent(matterRef)}/client-form-review/${encodeURIComponent(active.form_instance_id)}`,
            "_blank",
            "noopener",
        );
    };

    // BM-H354: CaseMinder has never served a questionnaire download and does
    // not hold a document to serve. Finalising records where the fee earner's
    // own DOCX was filed (`sharepoint_upload_ref`), and the matter's
    // SharePoint folder is the authoritative store. So open the filed
    // document rather than re-rendering a second copy that would drift from
    // the one the client was actually sent.
    const filedUrl = (() => {
        const ref = active?.sharepoint_upload_ref;
        if (!ref) return "";
        return ref.web_url || ref.webUrl || ref.url || "";
    })();

    const openFiledDoc = () => {
        if (!filedUrl) return;
        window.open(filedUrl, "_blank", "noopener");
    };

    // BM-H3615: answers are locked once the client submits. A fee earner can
    // re-open the questionnaire for the client (wills and LPA). It says what
    // will happen, asks first, and shows the backend's own refusal if there is one.
    const [reopenBusy, setReopenBusy] = useState(false);
    const [reopenNote, setReopenNote] = useState("");
    const canReopen = (kind === "wills" || kind === "lpa") && !!active
        && ["submitted", "under_review"].includes((active.state || "").toLowerCase());

    const reopenForClient = async () => {
        if (!active) return;
        const ok = window.confirm(
            "Re-open this questionnaire for the client? They will be able to change their answers "
            + "and will need to submit again. Anything already filed to Drafts will be out of date.",
        );
        if (!ok) return;
        setReopenBusy(true);
        setError("");
        setReopenNote("");
        try {
            const { data } = await clientFormsApi.reopenForm(matterRef, active.form_instance_id);
            setReopenNote((data && data.message) || "Re-opened for the client.");
            if (onChanged) onChanged();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError((detail && detail.message) || errorMessage(e, "Could not re-open the questionnaire."));
        } finally {
            setReopenBusy(false);
        }
    };

    // BM-H3584: LPAs are printed and signed on paper, so the useful output is
    // the filled official OPG form. Prepare = fill, verify, and say what is
    // left to do by hand; the downloads appear only after that succeeds.
    const [opgForms, setOpgForms] = useState(null);
    const [opgBusy, setOpgBusy] = useState(false);

    const saveBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const prepareOpgForms = async () => {
        if (!active) return;
        setOpgBusy(true);
        setError("");
        try {
            const { data } = await clientFormsApi.getLpaFormsManifest(active.form_instance_id);
            setOpgForms(data);
        } catch (e) {
            setOpgForms(null);
            setError(errorMessage(e, "Could not prepare the OPG form."));
        } finally {
            setOpgBusy(false);
        }
    };

    // BM-H3597: the outcome of filing the forms to the matter's Drafts folder.
    // It is read from the form itself, so a failure at submit time shows here
    // without anyone having to click anything.
    const [draftsFiling, setDraftsFiling] = useState(null);
    const [draftsBusy, setDraftsBusy] = useState(false);
    const filing = draftsFiling || opgForms?.drafts_filing || active?.opg_forms_filing || null;
    // BM-H4062: UAT Part C step 7. When the forms were already filed at submit time, a
    // click replaced the status line with the same words, so nothing visibly happened.
    // The outcome of THIS click is shown next to the button, with the time and the files.
    const savedAt = (iso) => {
        const d = iso ? new Date(iso) : null;
        if (!d || Number.isNaN(d.getTime())) return "";
        const time = d.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
        const day = d.toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "long", year: "numeric" });
        return `${time} on ${day}`;
    };

    const fileToDrafts = async () => {
        if (!active) return;
        setDraftsBusy(true);
        setError("");
        try {
            const { data } = await clientFormsApi.fileLpaFormsToDrafts(active.form_instance_id);
            setDraftsFiling(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = (detail && detail.message) || errorMessage(e, "Could not file the OPG form to Drafts.");
            setDraftsFiling({ status: (detail && detail.code) || "upload_failed", message });
        } finally {
            setDraftsBusy(false);
        }
    };

    // BM-H3671: LP3 (one per person to be told) and LPA120 (help with the fee).
    const downloadRegistrationForm = async (which, index) => {
        try {
            const id = active.form_instance_id;
            const { data } = which === "lpa120"
                ? await clientFormsApi.downloadLpaLpa120Pdf(id)
                : which === "sheet"
                    ? await clientFormsApi.downloadLpaSigningSheetPdf(id)
                    : await clientFormsApi.downloadLpaLp3Pdf(id, index);
            saveBlob(data, which === "lpa120" ? `LPA120_${matterRef}.pdf`
                : which === "sheet" ? `How-to-sign-your-LPA_${matterRef}.pdf`
                    : `LP3_${index}_${matterRef}.pdf`);
        } catch (e) {
            setError(errorMessage(e, "Could not download the form."));
        }
    };

    const downloadOpg = async (which) => {
        try {
            const call = which === "continuation"
                ? clientFormsApi.downloadLpaContinuationPdf
                : clientFormsApi.downloadLpaMainPdf;
            const { data } = await call(active.form_instance_id);
            const prefix = which === "continuation" ? "LPC" : (opgForms?.form_kind || "LP1").toUpperCase();
            saveBlob(data, `${prefix}_${matterRef}.pdf`);
        } catch (e) {
            setError(errorMessage(e, "Could not download the OPG form."));
        }
    };

    const canView     = !!active && ["submitted", "finalised"].includes((active.state || "").toLowerCase());
    // Finalising flips the state whether or not the upload ref landed, so
    // "finalised" on its own is not evidence there is anything to open.
    const canDownload = !!active && !!filedUrl
        && (active.state || "").toLowerCase() === "finalised";

    // BM-H3607: the Will draft. Assembled from approved clause wording and
    // filed to the matter's Drafts folder. The outcome is read from the form,
    // so a Will that could not be assembled at submit time says why here
    // without anyone having to click anything.
    const [willDraft, setWillDraft] = useState(null);
    const [willBusy, setWillBusy] = useState(false);
    const willFiling = willDraft || active?.will_draft_filing || null;
    // BM-H4285: a confirmation recorded after the result on screen may have cleared its reasons.
    const [confirmedSince, setConfirmedSince] = useState(false);
    // The outcome stored when the client submitted (not one from pressing the button here) says when it was.
    const storedAt = !willDraft && willFiling && willFiling.at ? new Date(willFiling.at) : null;
    const storedWhen = storedAt && !Number.isNaN(storedAt.getTime())
        ? `${storedAt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" })} at ${storedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}`
        : "";
    const willBlockers = (willFiling && Array.isArray(willFiling.blockers)) ? willFiling.blockers : [];

    const prepareWillDraft = async () => {
        if (!active) return;
        setWillBusy(true);
        setError("");
        setConfirmedSince(false);
        try {
            const { data } = await clientFormsApi.draftWill(matterRef, active.form_instance_id);
            setWillDraft(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = detail && detail.code;
            setWillDraft({
                status: code === "will_draft_blocked" ? "blocked" : (code === "upload_failed" ? "upload_failed" : "error"),
                message: (detail && detail.message) || errorMessage(e, "Could not prepare the Will draft."),
                blockers: (detail && detail.blockers) || [],
            });
        } finally {
            setWillBusy(false);
        }
    };

    return (
        <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"
                 data-testid={`matter-${kind}-questionnaire-card`}>
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal" />
                    {label} questionnaire
                </h2>
                <span className={`text-xs px-2 py-0.5 rounded border ${badgeClass}`}
                      data-testid={`matter-${kind}-questionnaire-state`}>
                    {stateBadge.text}
                </span>
            </div>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
                Send the client a secure link to complete the {label} questionnaire.
                {active && " Sending again creates a new questionnaire with its own link and emails it to the client. The earlier one is not changed or withdrawn."}
            </p>
            {error && (
                <p className="mt-3 text-sm text-status-red whitespace-pre-line"
                   data-testid={`matter-${kind}-questionnaire-error`}>{error}</p>
            )}
            {reopenNote && (
                <p className="mt-3 text-sm text-slate-300"
                   data-testid={`matter-${kind}-questionnaire-reopen-note`}>{reopenNote}</p>
            )}
            {link && (
                <div className="mt-4 flex gap-2">
                    <Input readOnly value={link} aria-label={`${label} questionnaire link`}
                           data-testid={`matter-${kind}-questionnaire-link`}
                           className="bg-white text-slate-900" />
                    <Button type="button" variant="outline" onClick={copyLink}
                            data-testid={`matter-${kind}-questionnaire-copy-btn`}>
                        <Copy className="w-4 h-4 mr-1.5" />{copied ? "Copied" : "Copy"}
                    </Button>
                </div>
            )}
            {kind === "lpa" && (
                <LpaSendDialog
                    open={lpaDialogOpen}
                    onOpenChange={setLpaDialogOpen}
                    defaultClientEmail={defaultClientEmail}
                    defaultClientName={defaultClientName}
                    feeEarnerEmail={lpaFeeEarner?.fee_earner_email || ""}
                    feeEarnerName={lpaFeeEarner?.fee_earner_name || ""}
                    busy={busy}
                    onSend={sendLpa}
                />
            )}
            <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={handleSend} disabled={busy}
                        className="bg-teal hover:bg-teal"
                        data-testid={`matter-send-${kind}-questionnaire-btn`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          : <Send className="w-4 h-4 mr-2" />}
                    {active ? `Send a new ${label} questionnaire` : `Send ${label} questionnaire`}
                </Button>
                <Button type="button" variant="outline" onClick={openReview}
                        disabled={!canView}
                        data-testid={`matter-view-${kind}-questionnaire-btn`}>
                    View submission
                </Button>
                <Button type="button" variant="outline" onClick={openFiledDoc}
                        disabled={!canDownload}
                        title={canDownload
                            ? "Open the filed questionnaire in SharePoint"
                            : "Available once the questionnaire has been filed to SharePoint"}
                        data-testid={`matter-open-${kind}-questionnaire-filed-btn`}>
                    Open filed document
                </Button>
                {canReopen && (
                    <Button type="button" variant="outline" onClick={reopenForClient}
                            disabled={reopenBusy}
                            title="Let the client change their answers. They will need to submit again."
                            data-testid={`matter-reopen-${kind}-questionnaire-btn`}>
                        {reopenBusy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Re-open for client
                    </Button>
                )}
                {kind === "lpa" && (
                    <Button type="button" variant="outline" onClick={prepareOpgForms}
                            disabled={!canView || opgBusy}
                            title={canView
                                ? "Fill the official OPG form from the client's answers, ready to print"
                                : "Available once the client has submitted the questionnaire"}
                            data-testid="matter-lpa-opg-forms-btn">
                        {opgBusy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Prepare OPG form for printing
                    </Button>
                )}
            </div>
            {kind === "lpa" && canView && filing && (
                <p className={`mt-3 text-xs ${filing.status === "filed" ? "text-slate-300" : "text-status-red"}`}
                   data-testid="matter-lpa-drafts-status">
                    {filing.status === "filed"
                        ? "The filled OPG forms are in the matter's Drafts folder."
                        : filing.status === "partly_filed"
                            ? `Some OPG forms are in the Drafts folder, but not all. ${filing.message || ""}`
                            : `The OPG form is not in the Drafts folder. ${filing.message || ""}`}
                </p>
            )}
            {/* BM-H4141: after a refresh this line was all that remained. It said the forms were filed and gave
                no way to open them; the only "Open filed document" button is the questionnaire's own filed copy,
                greyed until the form is finalised. The links are read from the stored filing record. The panel
                below lists the same files after a save in this visit, so they are not listed twice. */}
            {kind === "lpa" && canView && filing && !draftsFiling
                && ["filed", "partly_filed"].includes(filing.status)
                && (filing.files || []).some((f) => f.success && f.web_url) && (
                <ul className="mt-1 list-disc pl-5 space-y-0.5 text-xs text-slate-200"
                    data-testid="matter-lpa-drafts-status-files">
                    {(filing.files || []).filter((f) => f.success && f.web_url).map((f) => (
                        <li key={f.name}>
                            <a href={f.web_url} target="_blank" rel="noreferrer" className="underline">{f.name}</a>
                        </li>
                    ))}
                </ul>
            )}
            {kind === "lpa" && opgForms && (
                <div className="mt-4 border border-white/10 rounded-md p-3" data-testid="matter-lpa-opg-forms-panel">
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" onClick={fileToDrafts}
                                disabled={draftsBusy}
                                data-testid="matter-lpa-drafts-btn">
                            {draftsBusy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Save to Drafts folder
                        </Button>
                        <Button type="button" onClick={() => downloadOpg("main")}
                                className="bg-teal hover:bg-teal"
                                data-testid="matter-lpa-opg-main-btn">
                            Download {(opgForms.form_kind || "").toUpperCase()} (PDF)
                        </Button>
                        {opgForms.has_continuation_sheets && (
                            <Button type="button" variant="outline" onClick={() => downloadOpg("continuation")}
                                    data-testid="matter-lpa-opg-continuation-btn">
                                Download continuation sheets (PDF)
                            </Button>
                        )}
                    </div>
                    {draftsFiling && ["filed", "partly_filed"].includes(draftsFiling.status) && (
                        <div className="mt-2 text-xs text-slate-200" data-testid="matter-lpa-drafts-saved">
                            <p className="flex items-center gap-1">
                                <CheckCircle2 className="w-4 h-4 text-status-green" />
                                Saved to the matter's Drafts folder{draftsFiling.at ? ` at ${savedAt(draftsFiling.at)}` : ""}.
                                {" "}Saving again replaces these files.
                            </p>
                            <ul className="mt-1 list-disc pl-5 space-y-0.5">
                                {(draftsFiling.files || []).filter((f) => f.success).map((f) => (
                                    <li key={f.name}>
                                        {f.web_url
                                            ? <a href={f.web_url} target="_blank" rel="noreferrer" className="underline">{f.name}</a>
                                            : f.name}
                                    </li>
                                ))}
                            </ul>
                            {draftsFiling.status === "partly_filed" && (
                                <p className="mt-1 text-status-red">{draftsFiling.message}</p>
                            )}
                        </div>
                    )}
                    {draftsFiling && !["filed", "partly_filed"].includes(draftsFiling.status) && (
                        <p className="mt-2 text-xs text-status-red" data-testid="matter-lpa-drafts-failed">
                            Not saved to the Drafts folder. {draftsFiling.message || ""}
                        </p>
                    )}
                    {((opgForms.lp3 || []).length > 0 || opgForms.lpa120 || opgForms.signing_sheet) && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="matter-lpa-registration-forms">
                            {(opgForms.lp3 || []).map((f) => (
                                <Button key={f.sequence} type="button" variant="outline"
                                        onClick={() => downloadRegistrationForm("lp3", f.sequence)}
                                        data-testid={`matter-lpa-lp3-btn-${f.sequence}`}>
                                    Download {f.label} (PDF)
                                </Button>
                            ))}
                            {opgForms.lpa120 && (
                                <Button type="button" variant="outline"
                                        onClick={() => downloadRegistrationForm("lpa120")}
                                        data-testid="matter-lpa-lpa120-btn">
                                    Download LPA120 fee help (PDF)
                                </Button>
                            )}
                            {opgForms.signing_sheet && (
                                <Button type="button" variant="outline"
                                        onClick={() => downloadRegistrationForm("sheet")}
                                        data-testid="matter-lpa-signing-sheet-btn">
                                    Download how to sign your LPA (PDF)
                                </Button>
                            )}
                        </div>
                    )}
                    {(opgForms.registration_forms_not_prepared || []).length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-xs text-status-red space-y-1"
                            data-testid="matter-lpa-forms-not-prepared">
                            {opgForms.registration_forms_not_prepared.map((n) => (
                                <li key={n.form}>{n.form} was not prepared: {n.message}</li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-3 text-xs text-slate-300 font-semibold">Still to do by hand</p>
                    <ul className="mt-1 list-disc pl-5 text-xs text-slate-400 space-y-1"
                        data-testid="matter-lpa-opg-notes">
                        {(opgForms.notes || []).map((n) => <li key={n}>{n}</li>)}
                    </ul>
                </div>
            )}
            {kind === "wills" && (
                <div className="mt-4">
                    <Button type="button" variant="outline" onClick={prepareWillDraft}
                            disabled={!canView || willBusy}
                            title={canView
                                ? "Assemble the Will draft and file it to the matter's Drafts folder"
                                : "Available once the client has submitted the questionnaire"}
                            data-testid="matter-wills-draft-btn">
                        {willBusy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Prepare Will draft
                    </Button>
                </div>
            )}
            {kind === "wills" && active?.form_instance_id && (
                <WillConfirmationsPanel matterRef={matterRef} formInstanceId={active.form_instance_id}
                                        onRecorded={() => setConfirmedSince(true)} />
            )}
            {kind === "wills" && canView && willFiling && (
                <div className={`mt-3 text-xs ${willFiling.status === "filed" ? "text-slate-300" : "text-status-red"}`}
                     data-testid="matter-wills-draft-status">
                    <p>
                        {willFiling.status === "filed"
                            ? "The Will draft is in the matter's Drafts folder."
                            // BM-H4285: never "there is no Will draft in Drafts": an earlier draft may be there, now out of date.
                            : storedWhen
                                ? `When the client submitted on ${storedWhen}, no Will draft was filed. ${willFiling.message || ""} Once the reasons below are dealt with, press 'Prepare Will draft'. Any draft already in Drafts is from earlier answers.`
                                : `No Will draft was filed. ${willFiling.message || ""}`}
                    </p>
                    {willFiling.status !== "filed" && confirmedSince && (
                        <p className="mt-1 text-amber-300" data-testid="matter-wills-draft-stale">
                            A confirmation has been recorded since this result. Press 'Prepare Will draft' to try again.
                        </p>
                    )}
                    {willFiling.status !== "filed" && willBlockers.length > 0 && (
                        <ul className="mt-1 list-disc pl-5 space-y-1" data-testid="matter-wills-draft-blockers">
                            {willBlockers.slice(0, 8).map((b, i) => (
                                <li key={`${b.code}-${b.clause}-${i}`}>
                                    {b.heading ? `${b.heading}: ` : ""}{b.message}
                                </li>
                            ))}
                            {willBlockers.length > 8 && <li>and {willBlockers.length - 8} more</li>}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}

// Brief 77 Drift 4 / 5 — Invoice and Payment requests reachable from the
// matter page (they were Dashboard-only). BM-H3723 (Vanessa, 5 Oct 2026):
// buttons only, no stage lists; each opens the request with this matter
// already filled in (useMatterPrefill reads ?matter=).
function MatterFinancePanel({ matterRef }) {
    const enc = encodeURIComponent(matterRef);
    return (
        <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5"
                 data-testid="matter-finance-panel">
            <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-4">
                <Receipt className="w-4 h-4 text-teal" />
                Finance Requests
            </h2>
            <div className="flex flex-wrap gap-3">
                <Link to={`/invoice-request?matter=${enc}`} data-testid="matter-invoice-request-card">
                    <Button variant="outline" size="sm" data-testid="matter-invoice-request-btn">
                        <Receipt className="w-3.5 h-3.5 mr-1.5" /> New Invoice Request
                    </Button>
                </Link>
                <Link to={`/payment-request?matter=${enc}`} data-testid="matter-payment-request-card">
                    <Button variant="outline" size="sm" data-testid="matter-payment-request-btn">
                        <CreditCard className="w-3.5 h-3.5 mr-1.5" /> New Payment Request
                    </Button>
                </Link>
            </div>
        </section>
    );
}

const API = process.env.REACT_APP_BACKEND_URL || "";

const NARROW_VIEWPORT_BREAKPOINT = 1280;

// AML Section 3 (PR #546) — humanise the 5 `company_cdd:*` reason enum
// codes that may now arrive in `sofGate.blockers`. The Section-2 banner
// reason rendering is extended in place per V's OQ5 ack (no new banner
// component, no new gate clause). Codes are matched on a `company_cdd:`
// prefix so the underlying superset can grow without re-touching this
// map for cosmetics — unknown codes fall through to the raw string.
export const SOF_BANNER_REASON_LABELS = {
    "company_cdd:missing_documents": "Company CDD documents missing or incomplete",
    "company_cdd:invalid_company_number": "Companies House number invalid (must be 8 digits or 2 letters + 6 digits)",
    "company_cdd:link_not_evidenced": "Link from client to company is not evidenced on file",
    "company_cdd:link_rationale_too_short": "Link rationale must be at least 60 characters",
    "company_cdd:link_doc_ref_missing": "Link / CDD-document SharePoint URL missing or not https://",
    // 28 Oct 2026: a signed-off source of funds needs a price and rows.
    "purchase_price_missing": "Purchase price not recorded",
    "no_evidence_rows": "No evidence of funds on file",
    "no_origin_rows": "No origin of funds recorded",
    "gap_rationale_too_short": "Evidence or origin falls short of the price and the shortfall is not explained",
};

export function humaniseSofBannerReason(code) {
    if (!code) return "";
    if (SOF_BANNER_REASON_LABELS[code]) return SOF_BANNER_REASON_LABELS[code];
    // Reasons arrive in the canonical shape `company_cdd:<code>:origin_row_<N>`
    // (Section 3 backend composer). Split the trailing row tag off before
    // looking up the human label so the same map covers every row index.
    const m = /^(company_cdd:[a-z_]+)(?::origin_row_(\d+))?$/.exec(code);
    if (m) {
        const label = SOF_BANNER_REASON_LABELS[m[1]];
        if (label) {
            return m[2] !== undefined ? `${label} (origin row ${m[2]})` : label;
        }
    }
    return code;
}

export default function MatterDetail() {
    const { matterRef } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // BM-H4109: the top bar Call note files to this matter while it is open.
    useEffect(() => {
        if (!matterRef) return undefined;
        setCurrentMatterRef(matterRef);
        return () => clearCurrentMatterRef(matterRef);
    }, [matterRef]);
    // BM-H375: the pipeline tracker and the homescreen both send AML rows here
    // as /matter/{ref}#thirdfort-review. React Router's client-side navigate()
    // does not perform the browser's native fragment scroll, so before this the
    // fee earner arrived at the top of a long page with the Thirdfort panel
    // well below the fold and no sign anything had happened. The panel mounts
    // after its own fetch resolves, so poll briefly rather than looking once.
    useEffect(() => {
        const hash = location.hash;
        if (!hash || hash.length < 2) return undefined;
        const id = decodeURIComponent(hash.slice(1));
        let attempts = 0;
        const timer = setInterval(() => {
            attempts += 1;
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                clearInterval(timer);
            } else if (attempts >= 40) {
                clearInterval(timer);
            }
        }, 150);
        return () => clearInterval(timer);
    }, [location.hash, matterRef]);
    const [features, setFeatures] = useState({ sof_module_enabled: false });
    // Vanessa, 5 Oct 2026: no time recording on a pre-open matter. Unknown
    // until the overview loads, and hidden until then.
    const [matterOpen, setMatterOpen] = useState(false);
    // BM-H3443: bumped when a manual CCL send is recorded, so the banner re-reads.
    const [nextStepKey, setNextStepKey] = useState(0);
    // Safe default off: only render the client SHA questionnaire after the
    // authenticated FORF prefill confirms this matter is Company & Commercial.
    // BM-H fix (21Aug2026): the prefill call failing for ANY reason (conflicts
    // not cleared, no onboarding/conflict-check record yet, session expiry,
    // 5xx) used to collapse into the same blank practiceArea as a genuinely
    // non-COCO matter — the card just vanished with no explanation, which is
    // how Himanie's SHA questionnaire UAT got stuck at step 2. `status` now
    // carries WHY the card isn't showing so we can surface a reason instead
    // of silence. Values: "loading" | "ok" | "not_coco" | "blocked_conflicts"
    // | "no_record" | "error".
    const [shaQuestionnaireGate, setShaQuestionnaireGate] = useState({
        matterRef: null,
        practiceArea: "",
        status: "loading",
    });
    const [cmraGate, setCmraGate] = useState(null);  // {passed, blockers, summary_line, override_active, ...}
    // BM-H670 — blockers the matter has actually reached; falls back to all.
    const cmraDueBlockers = (cmraGate && (cmraGate.due_blockers || cmraGate.blockers)) || [];
    // BM-H670 (Vanessa, 29 Sep 2026): "passing AML should lead to a button Go
    // to FORF & Risk Assessment otherwise the user has to hunt it out from the
    // pipeline". Once CDD is done and no risk assessment is on file, offer the
    // FORF directly. Not shown when the red banner already carries its own
    // Complete Risk Assessment button, or under an MLCO override.
    const showGoToForf = Boolean(
        cmraGate && cmraGate.cdd_complete && !cmraGate.cmra_complete
        && !cmraGate.override_active && !cmraDueBlockers.includes("cmra_not_submitted"),
    );
    const goToForf = () => navigate(`/forf/${encodeURIComponent(matterRef)}?step=risk`);
    // BM-H671 (Vanessa, 29 Sep 2026): "nothing prompts the fee earner when the
    // client returns the OB". Once the matter is waiting on the AML check,
    // say so at the top of the page and take them to the Thirdfort panel.
    const showGoToAml = Boolean(
        cmraGate && cmraGate.pipeline_stage === "aml_check" && !cmraGate.cdd_complete,
    );
    const goToAml = () => {
        const el = typeof document !== "undefined" && document.getElementById("thirdfort-review");
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // BM-H543 put a "Complete Risk Assessment" form inline on this page.
    // BM-H593 (23 Sep 2026): the FORF is now the only way in. The red button
    // navigates to /forf/{ref}?step=risk, where Section 2 asks the same CMRA
    // questions (shared components/risk/CmraQuestions.jsx) and writes the one
    // record every reader uses. No inline form is mounted here any more.
    const [sofGate, setSofGate] = useState(null);    // AML R2 — SoF gate {passed, blockers, summary_line, override_active, gap_present, totals}
    // ── AML Section 4 — CDD renewal status ──────────────────────
    // {status, latest_cmra_band, latest_cmra_at, renewal_due_at,
    //  days_until_due, reason}. `error: true` means the endpoint
    // returned a 5xx — surface the "Renewal status unavailable" chip
    // per §3.8 (NO silent fallback).
    const [renewalStatus, setRenewalStatus] = useState(null);
    // BM-H90 — Private Client cards on this matter. `privateClientForms`
    // caches the current list of Wills / LPA / EPR form instances so each
    // card can render a state badge (Not sent / Sent / Submitted / Finalised).
    // `privateClientGate` decides whether to render the three cards at all;
    // Vanessa's rule is "always visible for their matter type", i.e. every
    // matter whose practice area code resolves to 008 (Private Client).
    const [privateClientForms, setPrivateClientForms] = useState([]);
    // BM-H3936: `loaded` and `type` / `failed` so a Private Client matter whose cards are hidden SAYS why,
    // instead of showing nothing (the cards used to vanish silently if the matter type was not exactly
    // "08 Private Client" or the lookup failed).
    const [privateClientGate, setPrivateClientGate] = useState({ visible: false, loaded: false, type: "", failed: false });
    const [prefillClientEmail, setPrefillClientEmail] = useState("");   // BM-H3707
    const [prefillClientName, setPrefillClientName] = useState("");       // BM-H4005
    const [isWideViewport, setIsWideViewport] = useState(() => (
        typeof window !== "undefined" && window.innerWidth >= NARROW_VIEWPORT_BREAKPOINT
    ));

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onResize = () => setIsWideViewport(window.innerWidth >= NARROW_VIEWPORT_BREAKPOINT);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const fetchFeatures = async () => {
        // Tenant feature flags — drives conditional rendering of the SoF
        // Review placeholder.  404 / network error → default-off (the
        // safe default per the v3 feature-flag contract).
        try {
            const r = await axios.get(`${API}/api/tenant/features`);
            setFeatures(r.data || {});
        } catch (_e) {
            setFeatures({ sof_module_enabled: false });
        }
    };

    const fetchCmraGate = async () => {
        // AML Section 1 — Golden Rule gate status for the matter view.
        try {
            const r = await axios.get(`${API}/api/risk/cmra/gate-status/${encodeURIComponent(matterRef)}`);
            setCmraGate(r.data || null);
        } catch (_e) {
            setCmraGate(null);
        }
    };

    const fetchSofGate = async () => {
        // AML Section 2 — SoF gate status. Composes (never mutates) the
        // CMRA gate; surfaces below the CMRA banner. Failure mode: null
        // → no SoF banner (same safe-default as CMRA gate).
        try {
            const r = await axios.get(`${API}/api/sof/depth/gate-status/${encodeURIComponent(matterRef)}`);
            setSofGate(r.data || null);
        } catch (_e) {
            setSofGate(null);
        }
    };

    const fetchRenewalStatus = async () => {
        // AML Section 4 — CDD renewal status. Failure mode per §3.8:
        // surface "Renewal status unavailable — refresh" chip rather
        // than silently hiding the banner.
        try {
            const r = await axios.get(`${API}/api/risk/cmra/renewal-status/${encodeURIComponent(matterRef)}`);
            setRenewalStatus(r.data || null);
        } catch (_e) {
            setRenewalStatus({ error: true });
        }
    };

    useEffect(() => { fetchFeatures(); fetchCmraGate(); fetchSofGate(); fetchRenewalStatus(); }, [matterRef]); // eslint-disable-line react-hooks/exhaustive-deps

    // BM-H525 (14 Sep 2026) — LinkAMLModal (used from PipelineTrackerCore, a
    // different route) writes bonsai_pipeline_cache_invalidated_at after a
    // successful AML link/override. Without this listener the CMRA/SoF/
    // renewal gate badges above kept showing the pre-approval state until a
    // hard refresh, because this page fetched them once on mount only.
    // Shared with PipelineTrackerCore.jsx — see
    // frontend/src/hooks/usePipelineCacheInvalidation.js.
    const refetchGateStatus = useCallback(() => {
        fetchCmraGate();
        fetchSofGate();
        fetchRenewalStatus();
    }, [matterRef]); // eslint-disable-line react-hooks/exhaustive-deps
    usePipelineCacheInvalidation(refetchGateStatus);

    // BM-H90 — fetch existing form instances + decide whether to show the
    // Private Client cards. Practice-area detection reuses the FORF prefill
    // (same call the SHA card uses). "08 Private Client" is the canonical
    // matter_type string from hub/matter_type_taxonomy.py.
    // BM-H350: wills and LPA questionnaires live in form_instances; the
    // Estate Planning Review one lives in its own collection behind the
    // workbook router. Both lists are needed or the review card reports
    // "Not sent" for a link the client has already been sent. Each side is
    // settled independently so one failing does not blank the other.
    const fetchPrivateClientForms = async () => {
        const [willsLpa, epr] = await Promise.allSettled([
            clientFormsApi.listForMatter(matterRef),
            clientFormsApi.listEprForMatter(matterRef),
        ]);
        const rowsOf = (settled) => {
            if (settled.status !== "fulfilled") return [];
            const d = settled.value?.data;
            return Array.isArray(d) ? d : (d?.forms || []);
        };
        setPrivateClientForms([...rowsOf(willsLpa), ...rowsOf(epr)]);
    };
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const response = await axios.get(`${API}/api/forf/${encodeURIComponent(matterRef)}/prefill`);
                if (!active) return;
                const practiceArea = (response.data?.prefill?.matter_type || "").trim();
                setPrivateClientGate({ visible: practiceArea === "08 Private Client", loaded: true, type: practiceArea, failed: false });
                setPrefillClientEmail((response.data?.prefill?.client_email || "").trim());
                setPrefillClientName((response.data?.prefill?.client_name || "").trim());
            } catch (_e) {
                // BM-H3978: the questionnaires are gated on this call. A 404 or 409 is the server REFUSING (no
                // conflict or onboarding record, conflict check not cleared, onboarding or AML not done), which is
                // not "could not be read". Keep what the server said so the notice can say it.
                const status = _e?.response?.status || 0;
                const d = _e?.response?.data?.detail;
                const said = typeof d === "string" ? d
                    : (d && typeof d === "object")
                        ? [d.status_plain, d.message, ...(Array.isArray(d.outstanding) ? d.outstanding : [])]
                            .filter((x) => typeof x === "string" && x).join(" ")
                        : "";
                if (active) setPrivateClientGate({ visible: false, loaded: true, type: "", failed: true, status, said });
            }
        })();
        fetchPrivateClientForms();
        return () => { active = false; };
    }, [matterRef]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let active = true;
        setShaQuestionnaireGate({ matterRef, practiceArea: "", status: "loading" });

        (async () => {
            try {
                const response = await axios.get(`${API}/api/forf/${encodeURIComponent(matterRef)}/prefill`);
                if (active) {
                    const practiceArea = (response.data?.prefill?.matter_type || "").trim();
                    setShaQuestionnaireGate({
                        matterRef,
                        practiceArea,
                        status: practiceArea === "02 COCO" ? "ok" : "not_coco",
                    });
                }
            } catch (e) {
                if (!active) return;
                // Distinguish WHY the prefill failed so the card can say something
                // useful instead of just disappearing. 409 = conflicts check not
                // cleared yet (backend's own gate, see forf_combined.py
                // _conflict_check_cleared); 404 = no onboarding/conflict-check
                // record on file at all; anything else (5xx, session expiry,
                // network) is a genuine error worth a refresh prompt.
                const httpStatus = e?.response?.status;
                let status = "error";
                if (httpStatus === 409) status = "blocked_conflicts";
                else if (httpStatus === 404) status = "no_record";
                setShaQuestionnaireGate({ matterRef, practiceArea: "", status });
            }
        })();

        return () => { active = false; };
    }, [matterRef]);

    const shaQuestionnaireGateReady = shaQuestionnaireGate.matterRef === matterRef;
    const showShareholdersAgreementPanel = (
        shaQuestionnaireGateReady && shaQuestionnaireGate.status === "ok"
    );
    // We can't tell whether a matter is Company & Commercial work without
    // the FORF prefill succeeding — that's the same call that's broken when
    // this fires, so there's no independent practice-area source on this
    // page to gate on instead. Trade-off, made deliberately rather than by
    // accident: a non-COCO matter with no onboarding/conflicts record yet
    // will occasionally see this chip too. That's a far smaller cost than
    // the previous behaviour (a COCO matter's SHA questionnaire silently
    // vanishing with zero explanation, which is what stalled Himanie's UAT).
    //
    // BM-H141 (Pen SHA UAT unblock, 2026-09-01): flip the trade-off for
    // "not_coco" too. Same class of silent-hide bug that first stalled
    // Himanie — she can UAT on a non-COCO test matter and the card just
    // vanishes with no on-screen explanation. Show a chip that names the
    // detected practice area so the fee earner understands why the
    // questionnaire button isn't there and can pick a COCO test matter.
    const showShaQuestionnaireBlockedChip = (
        shaQuestionnaireGateReady
        && (shaQuestionnaireGate.status === "blocked_conflicts"
            || shaQuestionnaireGate.status === "no_record"
            || shaQuestionnaireGate.status === "not_coco"
            || shaQuestionnaireGate.status === "error")
    );

    return (
        <div className="min-h-screen bg-bonsai-bg" data-testid="matter-detail-page">
            {/* BM-H4308 (Vanessa, 14 Oct 2026): "increase the width and legibility of
                the matter card generally". max-w-5xl less the 380px side panel left
                about 600px for the page; max-w-7xl gives it about 850px. */}
            <div
                className={isWideViewport ? "max-w-7xl mx-auto px-6 py-8 pr-[380px]" : "max-w-7xl mx-auto px-6 py-8"}
                data-testid="matter-detail-content"
            >
                <Link to="/matters"
                      className="text-xs text-slate-500 hover:text-teal inline-flex items-center mb-2">
                    <ArrowLeft className="w-3 h-3 mr-1" /> Back to Open Matters
                </Link>
                {/* 27 Oct 2026 system-wide refresh: the identity band takes the
                    Bonsai Agents look. Band only; everything below is unchanged. */}
                <div className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 px-5 py-4 mb-6 flex items-center justify-between flex-wrap gap-3 shadow-xl shadow-indigo-950/40">
                    <div className="relative flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ring-1 ring-white/20 shadow-lg shadow-indigo-900/50"
                             style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6 55%, #ec4899)" }}>
                            <FolderOpen className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-2xl font-bold text-white leading-tight" data-testid="matter-detail-title">
                                Matter <span className="font-mono agents-title">{matterRef}</span>
                            </h1>
                            <MatterClientLine matterRef={matterRef} apiBase={API} />
                        </div>
                    </div>
                    <div className="relative flex gap-2">
                        {/* BM-H4761: a house-style memo with the heading block filled in. */}
                        <NewMemoDialog matterRef={matterRef} />
                        {/* BM-H5047: put a piece of work on the supervision board. */}
                        <SubmitForReviewDialog matterRef={matterRef} />
                        <Link to={`/matter/${matterRef}/ai-workspace`}>
                            <Button variant="outline" size="sm" data-testid="matter-ai-workspace-link">
                                AI Workspace
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* BM-H3978: the UAT mark, for every matter type, where it can be found. One component
                    (UatMatterToggle), also mounted under the client care letter editor. */}
                <MatterUatStrip matterRef={matterRef} />

                {/* BM-H3350: one line saying what to do next, or why it stopped. */}
                <NextStepBanner matterRef={matterRef} refreshKey={nextStepKey} />

                {/* BM-H3723: money bar, SharePoint folder, parties, invoices. */}
                <MatterOverviewCard matterRef={matterRef} refreshKey={nextStepKey}
                                    onLoaded={ov => setMatterOpen(ov?.open === true)}
                                    timeSlot={matterOpen ? (
                                        <MatterTimeCard matterRef={matterRef} embedded actions={
                                            // Vanessa, 27 Oct 2026: a link to an invoice request, with the
                                            // matter, client and reference already filled in (useMatterPrefill).
                                            <Link to={`/invoice-request?matter=${encodeURIComponent(matterRef)}`}
                                                  data-testid="matter-time-invoice-request">
                                                <Button variant="outline" size="sm">
                                                    <Receipt className="w-3.5 h-3.5 mr-1.5" /> Invoice request
                                                </Button>
                                            </Link>
                                        } />
                                    ) : null} />

                {/* Vanessa, 27 Oct 2026: "i don't see a billing/time card with
                    lawbillity button", then "move the time card up to the top third
                    of the page and merge with billing/invoice/WIP". Time (Log time,
                    the clock, From LawBillity) sits inside the card above, under the
                    money bar, once the matter is open (CCL signed: Vanessa, 5 Oct
                    2026). The invoice and payment requests follow it. */}
                <div className="mb-6">
                {/* Brief 77 Drift 4/5 — Invoice (7) + Payment (6) requests on the matter page. */}
                <MatterFinancePanel matterRef={matterRef} />
                </div>

                {/* AML Section 1 — Always-visible risk-summary line + gate banner.
                    Per the SRA hardening brief, every matter view surfaces:
                    (a) the current calculated band + assessor + most recent
                        re-assessment trigger (the summary_line from the backend),
                    (b) a Golden-Rule gate banner when CDD+CMRA isn't satisfied or
                        when an MLCO override is active. */}
                {/* BM-H670 — the red banner names only blockers the matter has
                    reached (due_blockers). CDD falls due after the Thirdfort
                    stage, the risk assessment after the FORF. The gate itself
                    is unchanged. Older responses without due_blockers fall
                    back to every blocker. */}
                {cmraGate && (
                <div className="mb-5" data-testid="matter-cmra-summary-line">
                    <p className="text-sm text-slate-300 font-medium" data-testid="cmra-summary-text">
                        {cmraGate.summary_line || "Risk: NOT YET ASSESSED"}
                    </p>
                    {!cmraGate.passed && !cmraGate.override_active && cmraDueBlockers.length > 0 && (
                        <div className="mt-2 flex items-start gap-2 p-3 bg-status-red/10 border border-status-red/30 rounded-md"
                             data-testid="cmra-gate-blocker-banner">
                            <ShieldX className="w-4 h-4 text-status-red flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-xs text-status-red">
                                <p className="font-semibold mb-0.5">CDD + CMRA Golden Rule — gate not passed</p>
                                <p className="text-status-red/80">
                                    Blockers: {cmraDueBlockers.join(", ")}.
                                    Substantive work is locked until both are complete (or MLCO override).
                                </p>
                                {/* BM-H593 — the legitimate way through cmra_not_submitted
                                    is FORF Section 2. No role gate: open to the whole
                                    team. The MLCO-only override banner below is untouched. */}
                                {cmraDueBlockers.includes("cmra_not_submitted") && (
                                    <Button
                                        size="sm"
                                        onClick={() => navigate(`/forf/${encodeURIComponent(matterRef)}?step=risk`)}
                                        className="mt-2 bg-status-red hover:bg-status-red text-white"
                                        data-testid="open-complete-risk-assessment-btn"
                                    >
                                        Complete Risk Assessment
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    {showGoToAml && (
                        <div className="mt-2 flex items-center gap-3 p-3 bg-teal/10 border border-teal/40 rounded-md"
                             data-testid="go-to-aml-card">
                            <ShieldAlert className="w-4 h-4 text-teal flex-shrink-0" />
                            <p className="flex-1 text-xs text-slate-200">
                                Onboarding is back. Next: the AML / Thirdfort check.
                            </p>
                            <Button
                                size="sm"
                                onClick={goToAml}
                                className="bg-teal hover:bg-teal/90 text-white"
                                data-testid="go-to-aml-btn"
                            >
                                Go to AML / Thirdfort
                            </Button>
                        </div>
                    )}
                    {showGoToForf && (
                        <div className="mt-2 flex items-center gap-3 p-3 bg-teal/10 border border-teal/40 rounded-md"
                             data-testid="go-to-forf-card">
                            <ShieldCheck className="w-4 h-4 text-teal flex-shrink-0" />
                            <p className="flex-1 text-xs text-slate-200">
                                AML is done. Next: the FORF, then the risk assessment (Section 2).
                            </p>
                            <Button
                                size="sm"
                                onClick={goToForf}
                                className="bg-teal hover:bg-teal/90 text-white"
                                data-testid="go-to-forf-btn"
                            >
                                Go to FORF &amp; Risk Assessment
                            </Button>
                        </div>
                    )}
                    {cmraGate.override_active && (
                        <div className="mt-2 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md"
                             data-testid="cmra-override-active-banner">
                            <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-xs text-amber-300">
                                <p className="font-semibold mb-0.5">MLCO override active</p>
                                <p className="text-amber-200/80">
                                    The CDD+CMRA Golden Rule gate has been overridden by a Money Laundering
                                    Compliance Officer. The override and rationale are recorded in the
                                    matter's audit trail.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* AML Section 2 — SoF gate summary line + banners.
                    Placement: below the CMRA banner, above the TA-forms panel
                    (per build_brief_aml_section_2_sof_depth.md Block 3 #9).
                    Composes — never mutates — the CMRA gate state. */}
                {sofGate && (
                <div className="mb-5" data-testid="matter-sof-summary-line">
                    <p className="text-sm text-slate-300 font-medium" data-testid="sof-summary-text">
                        {sofGate.summary_line || "SoF: NOT YET CAPTURED"}
                    </p>
                    {!sofGate.passed && !sofGate.override_active && (
                        <div className="mt-2 flex items-start gap-2 p-3 bg-status-red/10 border border-status-red/30 rounded-md"
                             data-testid="matter-sof-blocker-banner">
                            <ShieldX className="w-4 h-4 text-status-red flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-xs text-status-red">
                                <p className="font-semibold mb-0.5">Source of Funds — gate not passed</p>
                                <p className="text-status-red/80" data-testid="matter-sof-blocker-reasons">
                                    Blockers: {(sofGate.blockers || []).map(humaniseSofBannerReason).join(", ") || "—"}.
                                    Approval submission is locked until SoF depth, evidence trail and
                                    (where applicable) gap rationale are on file (or MLCO override).
                                </p>
                            </div>
                        </div>
                    )}
                    {sofGate.override_active && (
                        <div className="mt-2 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md"
                             data-testid="matter-sof-override-banner">
                            <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-xs text-amber-300">
                                <p className="font-semibold mb-0.5">MLCO override active — Source of Funds</p>
                                <p className="text-amber-200/80">
                                    The SoF Golden Rule gate has been overridden by a Money Laundering
                                    Compliance Officer. The override and rationale are recorded in the
                                    matter's audit trail.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* ── AML Section 4 — CDD renewal banner ──────────────
                    Status taxonomy (§3.4):
                      - overdue   → red banner + "Start renewal" CTA
                      - due_soon  → amber banner + "Start renewal" CTA
                      - current   → NO banner (no clutter; §3.8)
                      - no_cmra_on_file → no banner (covered by CMRA gate)
                    Failure: 5xx → "Renewal status unavailable — refresh"
                    chip per §3.8 (NO silent fallback).             */}
                {renewalStatus?.error && (
                    <div className="mb-5 p-3 bg-slate-500/10 border border-slate-500/30 rounded-md flex items-center gap-2"
                         data-testid="matter-cdd-renewal-error">
                        <ShieldAlert className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        <span className="text-xs text-slate-200">
                            Renewal status unavailable — refresh
                        </span>
                    </div>
                )}
                {renewalStatus && !renewalStatus.error && renewalStatus.status === "overdue" && (
                    <div className="mb-5 flex items-start gap-2 p-3 bg-status-red/10 border border-status-red/30 rounded-md"
                         data-testid="matter-cdd-renewal-overdue">
                        <ShieldAlert className="w-4 h-4 text-status-red flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-xs text-status-red">
                            <p className="font-semibold mb-0.5">CDD renewal overdue</p>
                            <p className="text-status-red/80">
                                {renewalStatus.latest_cmra_band} risk · review was due {Math.abs(renewalStatus.days_until_due)} day{Math.abs(renewalStatus.days_until_due) === 1 ? "" : "s"} ago ({renewalStatus.renewal_due_at?.slice(0, 10)}).
                            </p>
                        </div>
                        <a href={`/risk/cmra/${encodeURIComponent(matterRef)}?kind=renewal`}
                           className="ml-2 px-3 py-1 text-xs font-medium rounded bg-status-red text-white hover:bg-status-red/90"
                           data-testid="matter-cdd-renewal-start-btn">
                            Start renewal
                        </a>
                    </div>
                )}
                {renewalStatus && !renewalStatus.error && renewalStatus.status === "due_soon" && (
                    <div className="mb-5 flex items-start gap-2 p-3 bg-status-amber/10 border border-status-amber/30 rounded-md"
                         data-testid="matter-cdd-renewal-due-soon">
                        <ShieldAlert className="w-4 h-4 text-status-amber flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-xs text-status-amber">
                            <p className="font-semibold mb-0.5">CDD renewal due soon</p>
                            <p className="text-status-amber/80">
                                {renewalStatus.latest_cmra_band} risk · review due in {renewalStatus.days_until_due} day{renewalStatus.days_until_due === 1 ? "" : "s"} ({renewalStatus.renewal_due_at?.slice(0, 10)}).
                            </p>
                        </div>
                        <a href={`/risk/cmra/${encodeURIComponent(matterRef)}?kind=renewal`}
                           className="ml-2 px-3 py-1 text-xs font-medium rounded bg-status-amber text-white hover:bg-status-amber/90"
                           data-testid="matter-cdd-renewal-start-btn">
                            Start renewal
                        </a>
                    </div>
                )}

                {/* Brief 77 Drift 3 — FE-input stage card + gated Send Onboarding Link. */}
                <MatterOnboardingPanel matterRef={matterRef} apiBase={API} />

                {/* BM-H3851: AML waits for the Letter of Authority. */}
                <LoaStatusPanel matterRef={matterRef} />
                {/* BM-H282: the reachable home for the Thirdfort report
                    review. The fee earner approval shipped in BM-H274 but
                    its only caller was the conflict search post-save panel,
                    which cannot be reopened for a matter already on the
                    pipeline, so the approval had no surface at all. The
                    pipeline tracker now links here at #thirdfort-review. */}
                <ThirdfortReviewPanel
                    matterRef={matterRef}
                    onAmlSatisfied={refetchGateStatus}
                    onGoToForf={goToForf}
                />

                {/* BM-H3306: a director can vouch for the client's ID here
                    during file opening. Directors only; clears the AML gate. */}
                <DirectorVouchPanel
                    matterRef={matterRef}
                    onAmlSatisfied={refetchGateStatus}
                />

                {showShareholdersAgreementPanel && (
                    <MatterShareholdersAgreementPanel matterRef={matterRef} />
                )}

                {/* BM-H90 — Private Client questionnaire cards. Rendered for
                    every matter whose practice area resolves to Private Client
                    (matter type code 008), regardless of whether a form has
                    been sent yet. Vanessa's rule: cards should always be
                    visible for their matter type, not just when unsent. */}
                {/* BM-H3936: a matter whose reference carries the Private Client type code (008) but whose
                    recorded type is not "08 Private Client", or whose type could not be read, used to show no
                    questionnaire cards and no explanation. Say so. */}
                {!privateClientGate.visible && privateClientGate.loaded && /(^|\.)008(\.|$)/.test(matterRef || "") && (
                    <section className="mt-6 border border-amber-400/40 bg-amber-400/5 rounded-lg p-4 text-sm text-amber-100"
                             data-testid="matter-private-client-hidden">
                        <p className="font-semibold">The Private Client questionnaires are hidden on this matter</p>
                        <p className="mt-1 text-xs text-amber-100/80" data-testid="matter-private-client-hidden-reason">
                            {privateClientGate.failed && [404, 409].includes(privateClientGate.status)
                                ? `This reference has the Private Client type code (008), but the questionnaires are not shown yet because the server refused to confirm the matter's type: ${privateClientGate.said || "the earlier steps are not complete"}. They appear once the conflict check is cleared, the onboarding questionnaire is back and the AML check is complete.`
                                : privateClientGate.failed
                                ? "This reference has the Private Client type code (008), but the matter's type could not be read just now. Reload the page. If it keeps happening, tell the team."
                                : `This reference has the Private Client type code (008), but the matter's type is recorded as "${privateClientGate.type || "nothing"}". The Wills, LPA and Estate Planning Review questionnaires show only for "08 Private Client". Ask whoever opened the file to correct the matter type, then reload.`}
                        </p>
                    </section>
                )}

                {privateClientGate.visible && (
                    <>
                        <PrivateClientQuestionnaireCard
                            matterRef={matterRef}
                            kind="wills"
                            label="Wills"
                            initialForms={privateClientForms}
                            onChanged={fetchPrivateClientForms}
                            defaultClientName={prefillClientName}
                        />
                        <PrivateClientQuestionnaireCard
                            matterRef={matterRef}
                            kind="lpa"
                            label="LPA"
                            initialForms={privateClientForms}
                            onChanged={fetchPrivateClientForms}
                            defaultClientEmail={prefillClientEmail}
                            defaultClientName={prefillClientName}
                        />
                        <PrivateClientQuestionnaireCard
                            matterRef={matterRef}
                            kind="epr"
                            label="Estate Planning Review"
                            initialForms={privateClientForms}
                            onChanged={fetchPrivateClientForms}
                        />
                    </>
                )}

                {/* BM-H fix (21Aug2026): visible reason instead of the SHA
                    questionnaire card just silently not appearing — see the
                    shaQuestionnaireGate comment above for why we can't be
                    fully precise about which matters this affects. */}
                {showShaQuestionnaireBlockedChip && (
                    <div className="mt-6 p-3 bg-slate-500/10 border border-slate-500/30 rounded-md flex items-center gap-2"
                         data-testid="matter-sha-questionnaire-blocked">
                        <ShieldAlert className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        <span className="text-xs text-slate-200">
                            {shaQuestionnaireGate.status === "blocked_conflicts"
                                ? "Shareholders' agreement questionnaire unavailable — complete the conflicts check on this matter first."
                                : shaQuestionnaireGate.status === "no_record"
                                ? "Shareholders' agreement questionnaire unavailable — no onboarding or conflicts record found for this matter yet."
                                : shaQuestionnaireGate.status === "not_coco"
                                ? "Shareholders' agreement questionnaire is available on Company & Commercial (COCO) matters only."
                                : "Shareholders' agreement questionnaire status unavailable — refresh."}
                        </span>
                    </div>
                )}

                {/* BM-H4320 (Vanessa, 14 Oct 2026): a Pre-CaseMinder matter passes the
                    golden rule on the dates its steps were done before CaseMinder. */}
                <PreCaseMinderHistoryPanel matterRef={matterRef}
                                           onChange={() => setNextStepKey((k) => k + 1)} />

                {/* BM-H3443 (Vanessa, 2 Oct 2026): a CCL sent by hand and saved
                    into the matter is recorded here, so the pipeline knows it
                    went. Sent and accepted stay two separate records (BM-H160). */}
                <section className="mt-6 rounded-lg overflow-hidden border border-white/10"
                         data-testid="matter-ccl-sent-manually">
                    <h2 className="text-sm font-semibold text-white bg-white/[0.04] px-4 py-2">
                        Client care letter sent outside CaseMinder
                    </h2>
                    <CclSentOffSystem matterRef={matterRef}
                                      onChange={() => setNextStepKey((k) => k + 1)} />
                </section>

                {/* Brief 100 — File Snap "at a glance" card: attendance notes, manual entries and (BM-H4112) every other touchpoint on the matter. */}
                <MatterFileNotesCard matterRef={matterRef} />

                {/* BM-H4266 — the matter's SharePoint documents with a preview pane:
                    click a document to see it without opening it (Vanessa, 13 Oct 2026). */}
                <MatterDocumentsCard matterRef={matterRef} />

                {/* BM-H4482 — letters before claim linked to this matter from the Bonsai Pen tool. */}
                <LatePaymentMatterCard matterRef={matterRef} />

                {/* BM-H3558 (4 Oct 2026): the staff Source of Funds panel. The
                    golden rule chain requires SoF on residential purchases and
                    this panel is the only place it is recorded, but until now
                    only a "Coming in Phase 1a-Firm" placeholder was mounted
                    here, so no purchase could pass the gate. Shown when the
                    module is on and SoF applies to this matter. */}
                {features.sof_module_enabled && sofGate && !sofGate.not_applicable && (
                <div className="mt-6 scroll-mt-20" id="source-of-funds" data-testid="matter-sof-panel">
                    <SoFSection2
                        matterReference={matterRef}
                        matterValueGbp={sofGate.matter_value_gbp ?? null}
                        matterValueSource={sofGate.matter_value_source || ""}
                        priceRequired={sofGate.price_required !== false}
                        matterRiskBand={sofGate.matter_risk_band || ""}
                        latest={sofGate.latest || null}
                        maySign={!!sofGate.may_sign}
                        onSaved={() => fetchSofGate()}
                    />
                </div>
                )}
            </div>

            {/* Bonsai Mate — right-rail embedded on ≥1280px, slide-over below.
                Pre-scoped to this matter (picker locked, suggestions filtered,
                chat session id is mate_${userId}_matter_${matterRef}). */}
            <BonsaiMate context="matter" matterReference={matterRef} />
            <MatterAlertsPanel matterReference={matterRef} />
        </div>
    );
}
